import { useCurrency } from "@/lib/currency";
// Reserva de Maya contra Bókun Test: calendario, horarios, categorías y precios
// salen EXCLUSIVAMENTE del producto de prueba (testProductId). El pago usa el
// flujo sandbox ya validado (reserva temporal → Stripe test → webhook).
import { useEffect, useMemo, useState, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Minus, Plus, FlaskConical } from "lucide-react";

import { getBokunTestBookingScreen } from "@/lib/bokun-test-booking.functions";
import { getBokunLiveBookingScreen } from "@/lib/bokun-live-booking.functions";
import { TourV2BookingFlow } from "@/components/tour/v2/TourV2BookingFlow";
import type { TourV2Quote, TourV2Selection } from "@/components/tour/v2/tour-v2-selection";
import { formatPrice } from "@/lib/slug";
import { ms, setPendingMayaSale, trackMayaSale } from "@/lib/maya-sales";

export interface V2BarState {
  /** "from" antes de seleccionar; "total" en cuanto hay fecha, horario o viajeros. */
  mode: "from" | "total";
  amount: number | null;
  currency: string;
  ready: boolean;
  label: string;
}

/** Clave de traducción a partir del nombre y edades reales de la categoría. */
function catKey(title: string, minAge: number | null, maxAge: number | null): string | null {
  const t = title.toLowerCase();
  if (/infant|bebe|bebé|beb[eê]|nourr|kleinkind|neonat|infante/.test(t)) return "INFANT";
  if (/child|niñ|nin|enfant|kind|bambin|crian/.test(t)) return "CHILD";
  if (/senior|mayor/.test(t)) return "SENIOR";
  if (/teen|adolesc/.test(t)) return "TEENAGER";
  if (/adult/.test(t)) return "ADULT";
  if (maxAge != null && maxAge <= 3) return "INFANT";
  return null;
}

export function MayaSandboxBooking({
  testProductId,
  slug,
  lang,
  testOnly,
  source = "maya",
  env = "test",
  onBarChange,
  openRef,
}: {
  /** V2: estado único que refleja la barra móvil (misma selección y total). */
  onBarChange?: (b: V2BarState) => void;
  /** V2: la barra móvil abre el mismo flujo con la selección ya hecha. */
  openRef?: MutableRefObject<(() => void) | null>;
  /** "live": Bókun Producción + Stripe Live (acceso controlado en el servidor). */
  env?: "test" | "live";
  testProductId: string;
  slug: string;
  lang: string;
  testOnly: boolean;
  /** Servicio de reserva compartido: Maya (chat) o la página V2 del tour. */
  source?: "maya" | "v2";
}) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const isMaya = source === "maya";
  const { t } = useTranslation();
  const [monthOffset, setMonthOffset] = useState(0);
  const fetchTest = useServerFn(getBokunTestBookingScreen);
  const fetchLive = useServerFn(getBokunLiveBookingScreen);
  const fetchScreen = env === "live" ? fetchLive : fetchTest;
  const q = useQuery({
    queryKey: ["maya-sandbox-screen", env, testProductId],
    queryFn: () => fetchScreen({ data: { activityId: testProductId, days: 60 } }),
    staleTime: 60_000,
  });
  const screen = q.data && !("error" in q.data) ? q.data : null;
  const [date, setDate] = useState<string | null>(null);
  const [slotKey, setSlotKey] = useState<string | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [flowOpen, setFlowOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [failed, setFailed] = useState(false);

  const dates = useMemo(
    () => Array.from(new Set(screen?.slots.map((s) => s.date) ?? [])).slice(0, 21),
    [screen],
  );
  const daySlots = screen?.slots.filter((s) => s.date === date) ?? [];
  const slot = daySlots.find((s) => s.key === slotKey) ?? (daySlots.length === 1 ? daySlots[0] : null);
  const cats = screen?.product.pricingCategories ?? [];
  // Nunca se muestra al viajero texto que nombre al proveedor interno.
  const policies = (screen?.product.policies ?? []).filter((p) => !/b[óo]kun|sandbox|\bapi\b/i.test(p));
  const title = `${testOnly ? "Tour de prueba · " : ""}${screen?.product.title ?? ""}`;

  const quote: TourV2Quote | null = useMemo(() => {
    if (!slot) return null;
    const rows = slot.prices.map((p) => {
      const c = cats.find((x) => x.id === p.pricingCategoryId);
      const raw = c?.title ?? p.title ?? p.pricingCategoryId;
      const key = catKey(raw, c?.minAge ?? null, c?.maxAge ?? null);
      const tr = key ? (t(`tourV2.cat.${key}`, { defaultValue: "" }) as string) : "";
      return {
        id: p.pricingCategoryId,
        title: tr || raw,
        minAge: c?.minAge ?? null,
        maxAge: c?.maxAge ?? null,
        price: p.amount,
        currency: slot.currency,
      };
    });
    // Orden real por edad: Adultos, Niños, Infantes.
    rows.sort((a, b) => (b.minAge ?? -1) - (a.minAge ?? -1));
    const pax = rows.reduce((a, r) => a + (qty[r.id] ?? 0), 0);
    const total = rows.reduce((a, r) => a + (qty[r.id] ?? 0) * r.price, 0);
    const selection: TourV2Selection = {
      date: slot.date,
      startTimeId: slot.startTimeId != null ? Number(slot.startTimeId) : null,
      startTime: slot.startTime,
      qty,
    };
    const min = slot.minParticipants ?? 1;
    return { selection, rows, currency: slot.currency, total, pax, complete: pax >= Math.max(1, min) };
  }, [slot, qty, cats, t]);

  // Motivo exacto por el que aún no se puede reservar.
  const need = !date ? "needDate" : !slot ? "needTime" : !quote?.complete ? "needPax" : null;
  const ready = !need && !!screen?.bookingsEnabled;
  // "Desde": tarifa real de la categoría de mayor edad en la primera salida.
  const fromAmount = useMemo(() => {
    const s0 = screen?.slots[0];
    if (!s0) return null;
    const byAge = [...s0.prices].sort((a, b) => {
      const ca = cats.find((x) => x.id === a.pricingCategoryId);
      const cb = cats.find((x) => x.id === b.pricingCategoryId);
      return (cb?.minAge ?? -1) - (ca?.minAge ?? -1);
    });
    return byAge.find((p) => p.amount > 0)?.amount ?? null;
  }, [screen, cats]);
  const started = !!date || Object.values(qty).some((n) => n > 0);
  const barMode: "from" | "total" = started ? "total" : "from";
  const barAmount = started ? (quote?.total ?? 0) : fromAmount;
  const barCurrency = quote?.currency ?? screen?.slots[0]?.currency ?? "MXN";
  const ctaLabel = need
    ? (t(`tourV2.${need}`) as string)
    : (t("tourV2.bookNowCta", { defaultValue: "Reservar ahora" }) as string);
  const openFlow = () => {
    if (!ready) return;
    setStep(1);
    setFlowOpen(true);
  };
  useEffect(() => {
    onBarChange?.({ mode: barMode, amount: barAmount, currency: barCurrency, ready, label: ctaLabel });
  }, [onBarChange, barMode, barAmount, barCurrency, ready, ctaLabel]);
  useEffect(() => {
    if (openRef) openRef.current = openFlow;
  });

  // Calendario mensual (V2): solo días con salida real son seleccionables.
  const allDates = useMemo(() => Array.from(new Set(screen?.slots.map((s) => s.date) ?? [])), [screen]);
  const firstMonth = allDates[0] ? allDates[0].slice(0, 7) : null;
  const monthKey = useMemo(() => {
    if (!firstMonth) return null;
    const [y, m] = firstMonth.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + monthOffset, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  }, [firstMonth, monthOffset]);
  const lastMonth = allDates.length ? allDates[allDates.length - 1].slice(0, 7) : null;

  if (q.isLoading)
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (!screen)
    return (
      <div className="m-3 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
        {ms(lang, "error")}
        <button type="button" onClick={() => void q.refetch()} className="ml-2 underline">
          {ms(lang, "retry")}
        </button>
      </div>
    );

  return (
    <div
      className={
        isMaya
          ? "flex min-h-0 flex-1 flex-col"
          : "flex flex-col overflow-hidden rounded-3xl border border-border/70 bg-card shadow-sm"
      }
      data-testid={isMaya ? undefined : "v2-test-booking"}
    >
      <div
        className={
          isMaya
            ? "min-h-0 flex-1 space-y-3 overflow-y-auto p-3 text-[12px]"
            : "space-y-3 p-4 text-sm"
        }
      >
        <div className="flex items-start gap-3">
          {screen.product.photos[0] && (
            <img src={screen.product.photos[0]} alt="" className="h-16 w-20 shrink-0 rounded-xl object-cover" />
          )}
          <div className="min-w-0">
            {testOnly && (
              <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-900">
                <FlaskConical className="h-3 w-3" /> Tour de prueba
              </span>
            )}
            <h3 className="text-sm font-semibold leading-snug">{title}</h3>
            
          </div>
        </div>

        {!screen.bookingsEnabled && (
          <p className="rounded-xl bg-amber-50 p-2 text-amber-900">{screen.disabledMessage}</p>
        )}

        {isMaya ? (
          <section>
          <p className="mb-1 font-semibold">{ms(lang, "pickDate")}</p>
          {dates.length === 0 && <p className="text-muted-foreground">—</p>}
          <div className="flex flex-wrap gap-1.5" data-testid="maya-test-dates">
            {dates.map((dt) => (
              <button
                key={dt}
                type="button"
                onClick={() => {
                  setDate(dt);
                  setSlotKey(null);
                  trackMayaSale("maya_sale_date_selected", { tourSlug: slug, lang });
                }}
                className={`rounded-full border px-2.5 py-1 ${dt === date ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}
              >
                {new Date(`${dt}T12:00:00`).toLocaleDateString(lang, { day: "numeric", month: "short" })}
              </button>
            ))}
          </div>
        </section>
        ) : (
          <section data-testid="v2-calendar" aria-label={t("tourV2.pickDay", { defaultValue: "Elige tu día" }) as string}>
            <div className="mb-2 flex items-center justify-between">
              <p className="font-semibold">{t("tourV2.pickDay", { defaultValue: "Elige tu día" })}</p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={monthOffset === 0}
                  onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
                  aria-label={t("common.previous", { defaultValue: "Anterior" }) as string}
                  className="grid h-8 w-8 place-items-center rounded-full border disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="min-w-[8rem] text-center text-sm font-medium capitalize">
                  {monthKey
                    ? new Intl.DateTimeFormat(lang, { month: "long", year: "numeric", timeZone: "UTC" }).format(
                        new Date(`${monthKey}-01T00:00:00Z`),
                      )
                    : "—"}
                </span>
                <button
                  type="button"
                  disabled={!monthKey || !lastMonth || monthKey >= lastMonth}
                  onClick={() => setMonthOffset((o) => o + 1)}
                  aria-label={t("common.next", { defaultValue: "Siguiente" }) as string}
                  className="grid h-8 w-8 place-items-center rounded-full border disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            {monthKey && (() => {
              const [y, m] = monthKey.split("-").map(Number);
              const first = new Date(Date.UTC(y, m - 1, 1));
              const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
              const lead = (first.getUTCDay() + 6) % 7;
              const wk = new Intl.DateTimeFormat(lang, { weekday: "short", timeZone: "UTC" });
              const heads = Array.from({ length: 7 }, (_, i) => wk.format(new Date(Date.UTC(2026, 5, 1 + i))).slice(0, 2));
              const cells: (string | null)[] = [
                ...Array.from({ length: lead }, () => null),
                ...Array.from({ length: days }, (_, i) => `${monthKey}-${String(i + 1).padStart(2, "0")}`),
              ];
              return (
                <div className="grid grid-cols-7 gap-1 text-center text-xs">
                  {heads.map((h, i) => (
                    <span key={i} className="py-1 font-medium uppercase text-muted-foreground">{h}</span>
                  ))}
                  {cells.map((d, i) => {
                    if (!d) return <span key={i} />;
                    const ok = allDates.includes(d);
                    const on = d === date;
                    return (
                      <button
                        key={d}
                        type="button"
                        disabled={!ok}
                        aria-pressed={on}
                        onClick={() => {
                          setDate(d);
                          setSlotKey(null);
                        }}
                        className={`aspect-square rounded-full text-sm transition ${on ? "bg-primary font-bold text-primary-foreground" : ok ? "font-semibold hover:bg-accent" : "text-muted-foreground/40"}`}
                      >
                        {Number(d.slice(8))}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </section>
        )}

        {daySlots.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {daySlots.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSlotKey(s.key)}
                className={`rounded-full border px-2.5 py-1 ${slot?.key === s.key ? "border-primary bg-primary text-primary-foreground" : ""}`}
              >
                {s.startTime ?? "—"}
              </button>
            ))}
          </div>
        )}

        {quote && (
          <section className="space-y-1.5" data-testid="maya-test-categories">
            {quote.rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border px-2.5 py-2">
                <div>
                  <p className="font-medium">
                    {r.title}
                    {r.maxAge ? ` (${r.minAge ?? 0}–${r.maxAge})` : ""}
                  </p>
                  <p className="text-muted-foreground">{r.price === 0 ? ms(lang, "free") : formatPrice(r.price, r.currency)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label={`- ${r.title}`}
                    onClick={() => setQty((s) => ({ ...s, [r.id]: Math.max(0, (s[r.id] ?? 0) - 1) }))}
                    className="grid h-7 w-7 place-items-center rounded-full border"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-4 text-center font-semibold">{qty[r.id] ?? 0}</span>
                  <button
                    type="button"
                    aria-label={`+ ${r.title}`}
                    onClick={() => setQty((s) => ({ ...s, [r.id]: (s[r.id] ?? 0) + 1 }))}
                    className="grid h-7 w-7 place-items-center rounded-full border"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}

        {quote && quote.pax > 0 && (
          <section className="rounded-2xl border p-3">
            <p className="mb-1 text-[11px] font-semibold uppercase text-muted-foreground">{ms(lang, "summary")}</p>
            <p>{title}</p>
            <p>{[slot?.date ? new Date(`${slot.date}T12:00:00`).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) : null, slot?.startTime].filter(Boolean).join(" · ")}</p>
            <p>
              {quote.rows
                .filter((r) => (qty[r.id] ?? 0) > 0)
                .map((r) => `${qty[r.id]} × ${r.title}`)
                .join(", ")}
            </p>
            <p className="mt-1 font-bold">Total: {formatPrice(quote.total, quote.currency)}</p>
            <p>
              {ms(lang, "payOnline")}: {formatPrice(quote.total, quote.currency)}
            </p>
            {policies.length > 0 && (
              <p className="mt-1 text-muted-foreground">
                {ms(lang, "cancellation")}: {policies.join(" · ")}
              </p>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">{ms(lang, "noHold")}</p>
          </section>
        )}
        {failed && <p className="rounded-xl bg-amber-50 p-2 text-amber-900">{ms(lang, "error")}</p>}
        {env === "test" && <p className="text-center text-[10px] text-muted-foreground">{ms(lang, "sandbox")}</p>}
      </div>

      {!isMaya && (
        <div className="flex items-end justify-between gap-3 border-t px-4 pt-3" data-testid="v2-total">
          <div>
            <p className="text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground">
              {barMode === "total" ? t("tourV2.total", { defaultValue: "Total" }) : t("tour.from", { defaultValue: "Precio" })}
            </p>
            <p className="tour-display text-2xl leading-tight">
              {barAmount != null ? formatPrice(barAmount, barCurrency) : "—"}
            </p>
            <p className="text-xs text-muted-foreground">{barCurrency}</p>
          </div>
        </div>
      )}
      <div className={`${isMaya ? "" : "hidden lg:block "}border-t bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]`}>
        <button
          type="button"
          data-testid={isMaya ? undefined : "v2-book-cta"}
          disabled={isMaya ? !quote?.complete || !screen.bookingsEnabled : !ready}
          onClick={openFlow}
          className="h-12 w-full rounded-full bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50"
        >
          {isMaya
            ? quote?.complete
              ? `${ms(lang, "continueBooking")} · ${formatPrice(quote.total, quote.currency)}`
              : ms(lang, "pickDate")
            : ready && quote
              ? `${ctaLabel} · ${formatPrice(quote.total, quote.currency)}`
              : ctaLabel}
        </button>
      </div>

      <TourV2BookingFlow
        open={flowOpen}
        onOpenChange={(o) => {
          if (!o && step < 3) trackMayaSale("maya_sale_booking_abandoned", { tourSlug: slug, lang });
          setFlowOpen(o);
        }}
        step={step}
        onStepChange={setStep}
        tourTitle={title}
        image={screen.product.photos[0] ?? null}
        activityId={testProductId}
        rateId={slot?.rateId ?? null}
        currency={quote?.currency ?? "MXN"}
        priceFrom={null}
        pricingCategories={cats.map((c) => ({ ...c, title: c.title ?? c.id }))}
        minBookable={null}
        maxBookable={null}
        pickupAvailable={screen.product.pickupAvailable}
        pickupRequired={screen.product.pickupRequired}
        dropoffAvailable={screen.product.dropoffAvailable}
        dropoffRequired={screen.product.dropoffRequired}
        pickupOptions={screen.product.pickupPlaces
          .filter((p) => p.id)
          .map((p) => ({ id: p.id as string, title: p.title ?? "", address: null }))}
        cancellationPolicy={policies.join(" · ") || null}
        selection={quote?.selection ?? { date: null, startTimeId: null, startTime: null, qty }}
        onSelectionChange={() => setFlowOpen(false)}
        quote={quote}
        onQuoteChange={() => {}}
        ownCheckout
        env={env}
        extendedForm
        layerClassName={isMaya ? "z-[120]" : undefined}
        onBookingEvent={(ev) => {
          if (!isMaya) return;
          if (ev === "checkout_started") {
            setFailed(false);
            trackMayaSale("maya_sale_checkout_started", {
              tourSlug: slug,
              lang,
              pax: quote?.pax,
              amount: quote?.total,
              currency: quote?.currency,
            });
          } else if (ev === "payment_redirect") {
            setPendingMayaSale({
              tourSlug: slug,
              tourTitle: title,
              amount: quote?.total ?? null,
              currency: quote?.currency ?? "MXN",
              pax: quote?.pax ?? 0,
              date: slot?.date ?? null,
              startTime: slot?.startTime ?? null,
              startedAt: new Date().toISOString(),
            });
          } else {
            setFailed(true);
            trackMayaSale("maya_sale_payment_declined", { tourSlug: slug, lang });
          }
        }}
      />
    </div>
  );
}
